import {
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  DollarSign,
  Info,
  Trash2,
  X,
  Calendar,
} from 'lucide-react';
import { RequestRateStatus } from '../../../../../api/types/lecturers/price-rate/request-rate/request-rate-status';
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { BranchLecturerRatesGroupDB } from '../../../../../api/types/lecturers/user-details-lecturer';
import { LecturerRateDisplayType } from '../../../../../api/types/lecturers/price-rate/lecturer-rate-display-type';
import dateUtils from '../../../../../utils/date-format';
import { updateRateStatus } from '../operations/update-rate-status';
import { UserSummaryType } from '../../../../../api/types/user/user-summary.type';
import ProgressBarIndicator from '../../../../../component/progress-bar-indicator/progress-bar-indicator';
import { ContractFiles } from './contracts/contract-files';
import { UserPageLecturerRateAccountant } from './acountant/user-page-lecturer-rate-accountant';
import globalSettingsBranches from '../../../../../stores/global-settings-branches';
import appApi from '../../../../../api/app-api';
import { CourseBranchRateSummary } from '../../../../../api/types/reports/rates/rate-summery';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

export interface UserPageLecturerRateItemDatamodel {
  index: number;
  activity: BranchLecturerRatesGroupDB;
  user: UserSummaryType;
  onRefresh: () => Promise<void>;
  shouldDisplayFinancials: boolean;
}

export const UserPageLecturerRateItem: React.FC<
  UserPageLecturerRateItemDatamodel
> = ({ index, activity, user, onRefresh, shouldDisplayFinancials }) => {
  const [expandedActivity, setExpandedActivity] = useState<number | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState<string | null>(null);
  const [uploadingRate, setUploadingRate] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const signedFileInputRef = useRef<HTMLInputElement>(null);

  // Date range state (default to last 2 years)
  const [startDate, setStartDate] = useState<Date>(() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 2);
    return date;
  });
  const [endDate, setEndDate] = useState<Date>(new Date());

  // Rate summary data
  const [rateSummaryData, setRateSummaryData] = useState<
    CourseBranchRateSummary[]
  >([]);
  const [loadingRateSummary, setLoadingRateSummary] = useState<boolean>(false);

  // Fetch rate summary data
  useEffect(() => {
    const fetchRateSummary = async () => {
      if (activity.rates.length === 0) return;

      setLoadingRateSummary(true);
      try {
        const branchIds = globalSettingsBranches.branches.map((b) => b.id);
        const rateId = activity.rates[0].id; // Use first rate's ID
        const activityTypeId = activity.settingsActivityTypeId;

        const response = await appApi.getRateSummary(
          rateId,
          activity.branchId,
          user.id,
          branchIds,
          startDate,
          endDate,
          activityTypeId,
        );

        if (response.data.ok) {
          setRateSummaryData(response.data.body);
        }
      } catch (error) {
        console.error('Failed to fetch rate summary:', error);
      } finally {
        setLoadingRateSummary(false);
      }
    };

    fetchRateSummary();
  }, [activity, startDate, endDate]);

  // Transform data for DataTable
  const tableData = useMemo(() => {
    const rows: Array<{
      courseName: string;
      branchId: string;
      branchName: string;
      averageRating: number;
      totalRatings: number;
    }> = [];

    rateSummaryData.forEach((course) => {
      Object.entries(course.branches).forEach(([branchId, branchData]) => {
        const branch = globalSettingsBranches.branches.find(
          (b) => b.id === parseInt(branchId),
        );
        rows.push({
          courseName: course.course_name,
          branchId: branchId,
          branchName: branch?.branchName || branchId,
          averageRating: branchData.averageRating,
          totalRatings: branchData.totalRatings,
        });
      });
    });

    return rows;
  }, [rateSummaryData]);

  const calculateProgressIndex = (
    rate: BranchLecturerRatesGroupDB['rates'][0],
  ) => {
    // Step 1: After creation (always true if rate exists)
    let progressIndex = 0;

    // Step 2: After approved - REQUIRED for next steps
    if (rate.requestStatus === RequestRateStatus.Approved) {
      progressIndex = 1;

      // Step 3: After contract uploaded - ONLY if approved
      if (rate.contractUrl) {
        progressIndex = 2;

        // Step 4: After signed contract uploaded - ONLY if contract exists
        if (rate.signContractUrl) {
          progressIndex = 3;

          // Step 5: After setup in payroll system - ONLY if signed contract exists
          if (rate.signedContractDocId) {
            progressIndex = 4;
          }
        }
      }
    }

    return progressIndex;
  };

  const getDisplayTypeText = (displayType: LecturerRateDisplayType) => {
    switch (displayType) {
      case LecturerRateDisplayType.OneHour:
        return 'שעתי';
      case LecturerRateDisplayType.AcademicHour:
        return 'אקדמי';
    }
  };

  const formatDate = (date: Date) => {
    return dateUtils.getDisplayDateStr(date);
  };

  const getStatusColor = (status: RequestRateStatus) => {
    switch (status) {
      case RequestRateStatus.Approved:
        return '#059669'; // green-600
      case RequestRateStatus.Pending:
        return '#CA8A04'; // yellow-600
      default:
        return '#6B7280'; // gray-600
    }
  };

  const getStatusIcon = (status: RequestRateStatus) => {
    const iconStyle = { width: '20px', height: '20px' };
    switch (status) {
      case RequestRateStatus.Approved:
        return <CheckCircle style={iconStyle} />;
      case RequestRateStatus.Pending:
        return <AlertCircle style={iconStyle} />;
      default:
        return <Info style={iconStyle} />;
    }
  };

  const getStatusText = (status: RequestRateStatus) => {
    switch (status) {
      case RequestRateStatus.Approved:
        return 'מאושר';
      case RequestRateStatus.Pending:
        return 'ממתין';
      case RequestRateStatus.Denied:
        return 'נדחה';
      default:
        return 'אחר';
    }
  };

  const handleRateStatus = async (
    branchId: number,
    rateId: number,
    isApprove: boolean,
  ) => {
    try {
      await updateRateStatus(branchId, user.id, rateId, isApprove);
      // Refresh the data after successful status update
      await onRefresh();
    } catch (error) {
      console.error('Failed to update rate status:', error);
    }
  };

  const handleDeleteRate = async (rateId: number) => {
    try {
      // TODO: Implement actual delete API call
      console.log('Deleting rate:', rateId);
      // await deleteRate(rateId);
      setShowDeleteDialog(null);
      // You might want to refresh the data here
    } catch (error) {
      console.error('Failed to delete rate:', error);
    }
  };

  const handleDeleteFile = async (identifier: string) => {
    try {
      const [rateId, fileType] = identifier.split('-');
      // TODO: Implement actual delete file API call
      console.log('Deleting file:', fileType, 'for rate:', rateId);
      // await deleteRateFile(parseInt(rateId), fileType);
      setShowDeleteDialog(null);
      // You might want to refresh the data here
    } catch (error) {
      console.error('Failed to delete file:', error);
    }
  };

  const getDeleteDialogText = (identifier: string) => {
    const [, fileType] = identifier.split('-');
    if (fileType === 'contract') {
      return 'האם אתה בטוח שברצונך למחוק את החוזה? פעולה זו אינה הפיכה ותמחק את הקובץ מהמערכת.';
    } else {
      return 'האם אתה בטוח שברצונך למחוק את החוזה החתום? פעולה זו אינה הפיכה ותמחק את הקובץ מהמערכת.';
    }
  };

  const getDeleteDialogTitle = (identifier: string) => {
    const [, fileType] = identifier.split('-');
    return fileType === 'contract' ? 'מחיקת חוזה' : 'מחיקת חוזה חתום';
  };

  const handleFileUpload = (
    rateId: number,
    fileType: 'contract' | 'signed',
  ) => {
    setUploadingRate(rateId);
    if (fileType === 'contract') {
      fileInputRef.current?.click();
    } else {
      signedFileInputRef.current?.click();
    }
  };

  const onFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    fileType: 'contract' | 'signed',
  ) => {
    const file = event.target.files?.[0];
    if (!file || !uploadingRate) return;

    try {
      // TODO: Implement actual file upload API call
      console.log(
        'Uploading file:',
        file.name,
        'for rate:',
        uploadingRate,
        'type:',
        fileType,
      );
      // const formData = new FormData();
      // formData.append('file', file);
      // await uploadRateFile(uploadingRate, formData, fileType);

      setUploadingRate(null);

      // Refresh the data after successful upload
      await onRefresh();
    } catch (error) {
      console.error('Failed to upload file:', error);
    } finally {
      // Reset file input
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  return (
    <div
      key={index}
      style={{
        borderRadius: '0.5rem',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        border: '1px solid #E5E7EB',
        marginBottom: '1rem',
        overflow: 'hidden',
      }}
    >
      <button
        style={{
          width: '100%',
          padding: '1rem 1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: 'transparent',
          border: 'none',
          cursor: 'pointer',
          flexDirection: 'row-reverse',
        }}
        onClick={() =>
          setExpandedActivity(expandedActivity === index ? null : index)
        }
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            gap: '1rem',
            flex: 1,
          }}
        >
          <div style={{ textAlign: 'right', alignSelf: 'flex-start' }}>
            <h3
              style={{
                fontSize: '1.125rem',
                fontWeight: 500,
                color: '#111827',
                marginBottom: '0.25rem',
              }}
            >
              {activity.activityName}
            </h3>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: '#6B7280',
                fontSize: '0.875rem',
              }}
            >
              <span>{activity.branchName}</span>
              <span>•</span>
              <span>{activity.rates.length} תעריפים</span>
            </div>
          </div>
        </div>
        {expandedActivity === index ? (
          <ChevronUp
            style={{
              width: '20px',
              height: '20px',
              color: '#9CA3AF',
            }}
          />
        ) : (
          <ChevronDown
            style={{
              width: '20px',
              height: '20px',
              color: '#9CA3AF',
            }}
          />
        )}
      </button>

      {expandedActivity === index && (
        <div style={{ borderTop: '1px solid #E5E7EB' }}>
          {activity.rates.map((rate, rateIndex) => (
            <div
              key={rateIndex}
              style={{
                padding: '1rem 1.5rem',
                borderBottom: '1px solid #E5E7EB',
              }}
            >
              <ProgressBarIndicator
                currentIndex={calculateProgressIndex(rate)}
                labels={[
                  'בקשה נוצרה',
                  'אישור הנהלה',
                  'העלאת חוזה',
                  'חתימה על חוזה',
                  'הקמה במערכת שכר',
                ]}
              />
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '1rem',
                  alignItems: 'center',
                  marginBottom: '1rem',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <DollarSign
                    style={{
                      width: '20px',
                      height: '20px',
                      color: '#9CA3AF',
                    }}
                  />
                  <div>
                    <p
                      style={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: '#6B7280',
                      }}
                    >
                      תעריף
                    </p>
                    <p
                      style={{
                        fontSize: '1rem',
                        color: '#111827',
                        fontWeight: 600,
                      }}
                    >
                      ₪{rate.rate}
                    </p>
                  </div>
                  <div>
                    <p
                      style={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: '#6B7280',
                      }}
                    >
                      נסיעות
                    </p>
                    <p
                      style={{
                        fontSize: '1rem',
                        color: '#111827',
                        fontWeight: 600,
                      }}
                    >
                      {rate.travelRate !== null
                        ? `₪${rate.travelRate}`
                        : 'לא הוזנו נסיעות'}
                    </p>
                  </div>
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <Info
                    style={{
                      width: '20px',
                      height: '20px',
                      color: '#9CA3AF',
                    }}
                  />
                  <div>
                    <p
                      style={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: '#6B7280',
                      }}
                    >
                      פירוט
                    </p>
                    <p style={{ fontSize: '1rem', color: '#111827' }}>
                      {rate.requestDescription}
                    </p>
                  </div>
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <Clock
                    style={{
                      width: '20px',
                      height: '20px',
                      color: '#9CA3AF',
                    }}
                  />
                  <div>
                    <p
                      style={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: '#6B7280',
                      }}
                    >
                      סוג תצוגה
                    </p>
                    <p style={{ fontSize: '1rem', color: '#111827' }}>
                      {getDisplayTypeText(rate.displayType)}
                    </p>
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '1rem',
                  alignItems: 'center',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <Clock
                    style={{
                      width: '20px',
                      height: '20px',
                      color: '#9CA3AF',
                    }}
                  />
                  <div>
                    <p
                      style={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: '#6B7280',
                      }}
                    >
                      תוקף
                    </p>
                    <p style={{ fontSize: '1rem', color: '#111827' }}>
                      {formatDate(rate.startTime)} - {formatDate(rate.endTime)}
                    </p>
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <div
                    style={{
                      width: '120px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      color: getStatusColor(rate.requestStatus),
                    }}
                  >
                    {getStatusIcon(rate.requestStatus)}
                    <span
                      style={{
                        fontSize: '1rem',
                        fontWeight: 500,
                      }}
                    >
                      {getStatusText(rate.requestStatus)}
                    </span>
                  </div>
                  {rate.requestStatus === RequestRateStatus.Pending && (
                    <div
                      style={{
                        display: 'flex',
                        gap: '0.5rem',
                      }}
                    >
                      <button
                        onClick={() =>
                          handleRateStatus(activity.branchId, rate.id, true)
                        }
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: '#059669',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.375rem',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          transition: 'background-color 0.2s',
                        }}
                        onMouseOver={(e) =>
                          (e.currentTarget.style.backgroundColor = '#047857')
                        }
                        onMouseOut={(e) =>
                          (e.currentTarget.style.backgroundColor = '#059669')
                        }
                      >
                        <CheckCircle size={16} />
                        אישור
                      </button>
                      <button
                        onClick={() =>
                          handleRateStatus(activity.branchId, rate.id, false)
                        }
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: '#DC2626',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.375rem',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          transition: 'background-color 0.2s',
                        }}
                        onMouseOver={(e) =>
                          (e.currentTarget.style.backgroundColor = '#B91C1C')
                        }
                        onMouseOut={(e) =>
                          (e.currentTarget.style.backgroundColor = '#DC2626')
                        }
                      >
                        <AlertCircle size={16} />
                        דחייה
                      </button>
                    </div>
                  )}
                </div>

                <div
                  style={{
                    width: '359px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: '1rem',
                  }}
                >
                  {/* Regular Contract Actions */}
                  <ContractFiles
                    userId={user.id}
                    rateId={rate.id}
                    branchId={activity.branchId}
                    text={'חוזה:'}
                    color={'#F9FAFB'}
                    isSignContract={false}
                    contractUrl={rate.contractUrl}
                    onRefresh={onRefresh}
                  />
                  <ContractFiles
                    userId={user.id}
                    rateId={rate.id}
                    branchId={activity.branchId}
                    text={'חוזה\nחתום:'}
                    color={'#F0FDF4'}
                    isSignContract={true}
                    contractUrl={rate.signContractUrl}
                    onRefresh={onRefresh}
                  />
                </div>
              </div>
              <div>
                <UserPageLecturerRateAccountant rate={rate} />
                {/* Accounting Management Section */}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Hidden File Inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.doc,.docx"
        style={{ display: 'none' }}
        onChange={(e) => onFileUpload(e, 'contract')}
      />
      <input
        ref={signedFileInputRef}
        type="file"
        accept=".pdf,.doc,.docx"
        style={{ display: 'none' }}
        onChange={(e) => onFileUpload(e, 'signed')}
      />

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowDeleteDialog(null)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '0.75rem',
              padding: '2rem',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              transform: 'scale(1)',
              animation: 'fadeIn 0.2s ease-out',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '1.5rem',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                }}
              >
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: '#FEE2E2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <AlertCircle
                    style={{
                      width: '20px',
                      height: '20px',
                      color: '#EF4444',
                    }}
                  />
                </div>
                <h3
                  style={{
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    color: '#111827',
                    margin: 0,
                  }}
                >
                  {getDeleteDialogTitle(showDeleteDialog!)}
                </h3>
              </div>
              <button
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  borderRadius: '0.25rem',
                  color: '#9CA3AF',
                  transition: 'color 0.2s',
                }}
                onClick={() => setShowDeleteDialog(null)}
                onMouseOver={(e) => (e.currentTarget.style.color = '#6B7280')}
                onMouseOut={(e) => (e.currentTarget.style.color = '#9CA3AF')}
              >
                <X style={{ width: '20px', height: '20px' }} />
              </button>
            </div>

            <p
              style={{
                color: '#6B7280',
                fontSize: '1rem',
                lineHeight: '1.5',
                marginBottom: '2rem',
                textAlign: 'right',
              }}
            >
              {getDeleteDialogText(showDeleteDialog!)}
            </p>

            <div
              style={{
                display: 'flex',
                gap: '0.75rem',
                justifyContent: 'flex-end',
              }}
            >
              <button
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '1px solid #E5E7EB',
                  borderRadius: '0.5rem',
                  backgroundColor: 'white',
                  color: '#374151',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onClick={() => setShowDeleteDialog(null)}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#F9FAFB';
                  e.currentTarget.style.borderColor = '#D1D5DB';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'white';
                  e.currentTarget.style.borderColor = '#E5E7EB';
                }}
              >
                ביטול
              </button>
              <button
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '1px solid #EF4444',
                  borderRadius: '0.5rem',
                  backgroundColor: '#EF4444',
                  color: 'white',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
                onClick={() => handleDeleteFile(showDeleteDialog!)}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#DC2626';
                  e.currentTarget.style.borderColor = '#DC2626';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = '#EF4444';
                  e.currentTarget.style.borderColor = '#EF4444';
                }}
              >
                <Trash2 style={{ width: '16px', height: '16px' }} />
                {showDeleteDialog!.includes('contract')
                  ? 'מחק חוזה'
                  : 'מחק חוזה חתום'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Course/Branch Rate Summary Table */}
      {shouldDisplayFinancials && (
        <div
          style={{
            padding: '1.5rem',
            borderTop: '1px solid #E5E7EB',
            backgroundColor: '#FFFFFF',
          }}
        >
          <h3
            style={{
              fontSize: '1.125rem',
              fontWeight: 600,
              color: '#111827',
              marginBottom: '1rem',
              textAlign: 'right',
            }}
          >
            סיכום תעריפים לפי קורסים וסניפים
          </h3>

          {/* Date Range Inputs */}
          <div
            style={{
              padding: '1rem 1.5rem',
              backgroundColor: '#F9FAFB',
              border: '1px solid #E5E7EB',
              borderRadius: '0.5rem',
              marginBottom: '1.5rem',
              display: 'flex',
              gap: '1rem',
              alignItems: 'center',
              flexDirection: 'row-reverse',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              <Calendar
                style={{
                  width: '20px',
                  height: '20px',
                  color: '#6B7280',
                }}
              />
              <span
                style={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: '#374151',
                }}
              >
                טווח תאריכים לסיכום תעריפים:
              </span>
            </div>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.25rem',
                }}
              >
                <label
                  style={{
                    fontSize: '0.75rem',
                    color: '#6B7280',
                    textAlign: 'right',
                  }}
                >
                  תאריך התחלה
                </label>
                <input
                  type="date"
                  value={startDate.toISOString().split('T')[0]}
                  onChange={(e) => setStartDate(new Date(e.target.value))}
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #D1D5DB',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    direction: 'ltr',
                  }}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.25rem',
                }}
              >
                <label
                  style={{
                    fontSize: '0.75rem',
                    color: '#6B7280',
                    textAlign: 'right',
                  }}
                >
                  תאריך סיום
                </label>
                <input
                  type="date"
                  value={endDate.toISOString().split('T')[0]}
                  onChange={(e) => setEndDate(new Date(e.target.value))}
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #D1D5DB',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    direction: 'ltr',
                  }}
                />
              </div>
            </div>
          </div>

          <DataTable
            value={tableData}
            loading={loadingRateSummary}
            emptyMessage={
              <div
                style={{
                  textAlign: 'center',
                  padding: '2rem',
                  color: '#6B7280',
                }}
              >
                אין נתונים להצגה בטווח התאריכים שנבחר
              </div>
            }
            rowGroupMode="rowspan"
            groupRowsBy="course_name"
            sortField="course_name"
            sortOrder={1}
            tableStyle={{
              direction: 'rtl',
              fontSize: '0.875rem',
            }}
            stripedRows
          >
            <Column
              field="courseName"
              header="שם הקורס"
              style={{
                textAlign: 'right',
                fontWeight: 500,
                color: '#111827',
              }}
              headerStyle={{
                textAlign: 'right',
                fontWeight: 600,
                color: '#374151',
                backgroundColor: '#F9FAFB',
              }}
            />
            <Column
              field="branchName"
              header="סניף"
              style={{
                textAlign: 'right',
                color: '#6B7280',
              }}
              headerStyle={{
                textAlign: 'right',
                fontWeight: 600,
                color: '#374151',
                backgroundColor: '#F9FAFB',
              }}
            />
            <Column
              field="averageRating"
              header="ממוצע תעריף"
              body={(rowData) => `₪${rowData.averageRating.toFixed(2)}`}
              style={{
                textAlign: 'right',
                fontWeight: 500,
                color: '#111827',
              }}
              headerStyle={{
                textAlign: 'right',
                fontWeight: 600,
                color: '#374151',
                backgroundColor: '#F9FAFB',
              }}
            />
            <Column
              field="totalRatings"
              header='סה"כ דירוגים'
              style={{
                textAlign: 'right',
                color: '#6B7280',
              }}
              headerStyle={{
                textAlign: 'center',
                fontWeight: 600,
                color: '#374151',
                backgroundColor: '#F9FAFB',
              }}
            />
          </DataTable>
        </div>
      )}
    </div>
  );
};
